import React, { useRef, useEffect, useState } from 'react';
import { select } from 'd3-selection';
import { arc } from 'd3-shape';
import { interpolate } from 'd3-interpolate';
import 'd3-transition';
import styles from './index.module.scss';
import PerformanceMeterText from '@cp/src/assets/images/icons/PerformanceMeterText';
import { toFixedValueNotation } from '@cp/src/utils/commonUtils';
import { Tooltip, TooltipTrigger, TooltipContent } from '@cp/src/components/TooltipV2/TooltipV2';
import cx from 'classnames';
import TargetBarIcon from '@cp/src/assets/icons/TargetBarIcon';

interface PerformanceMeterProps {
  performance: number;
  width: number;
  height: number;
  startLimit: string;
  endLimit: string;
  bgColor?: string;
  duration?: number;
  title?: React.ReactNode;
  successAnimationLottie?: React.ReactNode;
  showTooltip?: boolean;
  tooltipContent?: React.ReactNode;
  target?: number;
  showTarget?: boolean;
  targetTooltipContent?: React.ReactNode;
  performancePercentage?: number;
}

type Position = {
  x: number;
  y: number;
};

const MAX_PERFORMANCE = 100;

const PerformanceMeter: React.FC<PerformanceMeterProps> = ({
  performance,
  width,
  height,
  startLimit,
  endLimit,
  bgColor = 'var(--green-border-1)',
  duration = 1000,
  title,
  successAnimationLottie,
  showTooltip = false,
  tooltipContent = '',
  target = 0,
  showTarget = false,
  targetTooltipContent = '',
  performancePercentage = 0
}) => {
  const ref = useRef<SVGSVGElement>(null);
  const [tooltipPosition, setTooltipPosition] = useState<Position | null>(null);
  const [targetPosition, setTargetPosition] = useState<Position | null>(null);
  const [targetRotation, setTargetRotation] = useState<number>(0);

  const showTooltipForPerformance = showTooltip && tooltipPosition;

  const maxPerformanceSvg = (performancePercentage || performance) >= MAX_PERFORMANCE;

  const showTooltipForTarget =
    showTarget && targetPosition && target > 0 && (performancePercentage || performance) < 100;

  useEffect(() => {
    const radius = Math.min(width, height) / 2;
    const thickness = 18;
    const innerRadius = radius - thickness;
    const outerRadius = radius;
    const centerX = width / 2;
    const centerY = height / 2;
    const svg = select(ref.current);

    svg.selectAll('*').remove();

    const g = svg
      .attr('width', width)
      .attr('height', height / 2 + 8)
      .append('g')
      .attr('transform', `translate(${centerX}, ${centerY})`);

    const backgroundArc = arc()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .startAngle(-Math.PI / 2)
      .endAngle(Math.PI / 2)
      .cornerRadius(40);

    g.append('path')
      .attr('d', backgroundArc as any)
      .attr('fill', '#e6e6e6')
      .attr('opacity', 1);

    const clipId = 'foregroundClip';
    svg
      .append('defs')
      .append('clipPath')
      .attr('id', clipId)
      .append('path')
      .attr('d', backgroundArc as any);

    const foregroundArc = arc()
      .innerRadius(innerRadius)
      .outerRadius(outerRadius)
      .startAngle(-Math.PI / 2)
      .cornerRadius(40);

    const path = g.append('g').attr('clip-path', `url(#${clipId})`);

    const arcPath = path
      .append('path')
      .datum({ endAngle: -Math.PI / 2 })
      .attr('d', foregroundArc as any)
      .attr('fill', bgColor);

    if (showTarget && target > 0) {
      const endLimitValue = parseInt(endLimit.replace(/[^0-9]/g, ''), 10);
      if (endLimitValue > 0) {
        let targetPerformance = (target / endLimitValue) * 100;
        // cap the target performance at 97% bcz after this its overflowing the meter
        if (targetPerformance >= 97) {
          targetPerformance = 97;
        }
        const targetEndAngle = Math.PI * (targetPerformance / 100 - 0.5);
        const targetAngle = targetEndAngle - Math.PI / 2;
        const targetX = centerX + outerRadius * Math.cos(targetAngle);
        const targetY = centerY + outerRadius * Math.sin(targetAngle);
        setTargetPosition({ x: targetX, y: targetY });

        const rotationDegrees = (targetAngle * 180) / Math.PI + 90;
        setTargetRotation(rotationDegrees);
      }
    }

    arcPath
      .transition()
      .duration(duration)
      .attrTween('d', (d: any) => {
        const interpolateAngle = interpolate(
          d.endAngle,
          Math.PI * ((performance >= 100 ? 1 : performance / 100) - 0.5)
        );

        return t => {
          d.endAngle = interpolateAngle(t);
          const angle = interpolateAngle(t) - Math.PI / 2;
          const tooltipX = centerX + outerRadius * Math.cos(angle);
          const tooltipY = centerY + outerRadius * Math.sin(angle);
          setTooltipPosition({ x: tooltipX, y: tooltipY });

          return foregroundArc(d) as string;
        };
      });
  }, [performance, width, height, duration, target, showTarget, endLimit]);

  return (
    <div style={{ width }} className="relative mb-7">
      <svg ref={ref}></svg>
      <div className="flex justify-between">
        <div className={styles.meterLimit}>{startLimit}</div>
        <div className={styles.meterLimit}>{endLimit}</div>
      </div>

      {maxPerformanceSvg ? (
        showTooltip ? (
          <div
            className="absolute z-[var(--z-index-side-logout-btn)]"
            style={{
              left: `${targetPosition?.x}px`,
              top: `${targetPosition?.y}px`,
              transform: `translate(-50%, -10%)`
            }}
          >
            <div className="relative">
              <Tooltip placement="top">
                <TooltipTrigger asChild>
                  <PerformanceMeterText />
                  <div className={styles.performanceText}>
                    <div>
                      {toFixedValueNotation(String(performancePercentage || performance), 0)}
                    </div>
                  </div>
                </TooltipTrigger>
                <TooltipContent className={cx('tooltipWrapper', 'absolute !top-[-50px]')}>
                  {targetTooltipContent}
                </TooltipContent>
              </Tooltip>
            </div>
          </div>
        ) : (
          <div className="absolute bottom-[-36px] right-[-36px]">
            <div>
              <PerformanceMeterText />
              <div className={styles.performanceText}>
                <div>{toFixedValueNotation(String(performance), 0)}</div>
              </div>
            </div>
          </div>
        )
      ) : null}

      {showTooltipForPerformance && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="absolute w-5 h-5 rounded-full z-[var(--z-index-side-logout-btn)]"
              style={{
                left: `${tooltipPosition.x}px`,
                top: `${tooltipPosition.y}px`,
                transform: 'translate(-70%, -20%)'
              }}
            />
          </TooltipTrigger>
          <TooltipContent className="tooltipWrapper">{tooltipContent}</TooltipContent>
        </Tooltip>
      )}

      {showTooltipForTarget && (
        <Tooltip>
          <TooltipTrigger asChild>
            <div
              className="absolute"
              style={{
                left: `${targetPosition.x}px`,
                top: `${targetPosition.y}px`,
                transform: `translate(-50%, -0%) rotate(${targetRotation}deg)`
              }}
            >
              <TargetBarIcon fillColor="var(--green-dark)" height={24} />
            </div>
          </TooltipTrigger>
          <TooltipContent className="tooltipWrapper">{targetTooltipContent}</TooltipContent>
        </Tooltip>
      )}

      <div className={styles.meterText}>{title}</div>
      {performancePercentage >= MAX_PERFORMANCE ? successAnimationLottie : null}
    </div>
  );
};

export default PerformanceMeter;
